import axios, { AxiosError } from "axios";

const apiEndpoint = process.env;
console.log("🚀 ~ apiEndpoint:", apiEndpoint);

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
  (config) => {
    // const token = localStorage.getItem("accessToken");
    // if (token) {
    //   config.headers.Authorization = `bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Type definitions
export interface MerchantSubscriptionPreferences {
  BirthdayClubEmail: boolean;
  YourActivityEmail: boolean;
  ExclProductOffersEmail: boolean;
  MarketingEmail: boolean;
  FeedbackEmail: boolean;
}

export interface RewardleSubscriptionPreferences {
  GenRewardleUpdEmail: boolean;
  PersonalActivityEmail: boolean;
  ExclProductOffersEmail: boolean;
  SecurityUpdEmail: boolean;
  PostcoderNewsletterEmail: boolean;
}

export interface MerchantSubscriptionData {
  merchantName: string;
  patronEmail: string;
  merchantId: number;
  patronId: number;
  merchantLogo?: string;
  preferences: MerchantSubscriptionPreferences;
}

export interface RewardleSubscriptionData {
  email: string;
  logo?: string;
  preferences: RewardleSubscriptionPreferences;
}

export interface EmailLogData {
  transactionId: string;
  merchantId: number;
  patronId: number;
  toEmail: string;
  sentDate: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

// Helper function to handle API errors
const handleApiError = (error: unknown, defaultMessage: string): never => {
  const fallbackMessage =
    "There was problem while processing the request. Please try again later or contact Rewardle Support at support@rewardle.com";

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    // Prefer friendlier message for server-side errors (e.g. 500)
    if (axiosError.response?.status && axiosError.response.status >= 500) {
      throw new Error(fallbackMessage);
    }

    const message =
      axiosError.response?.data?.message ||
      axiosError.message ||
      defaultMessage ||
      fallbackMessage;

    throw new Error(message);
  }

  throw new Error(defaultMessage || fallbackMessage);
};

/**
 * Fetch merchant subscription preferences
 * @param transactionId - Optional transaction ID from email log
 * @param euid - External unique identifier
 */
export const getMerchantSubscription = async (
  transactionId?: string,
  euid?: string
): Promise<MerchantSubscriptionData> => {
  try {
    const params: Record<string, string> = {};
    if (transactionId?.trim()) params.transactionId = transactionId.trim();
    if (euid?.trim()) params.euid = euid.trim();

    const response = await axiosInstance.get<MerchantSubscriptionData>(
      `emailmgmt/subscription/merchant`,
      {
        params
      }
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error,
      "Failed to load merchant subscription preferences"
    );
  }
};

/**
 * Update merchant subscription preferences
 * @param merchantId - Merchant ID
 * @param patronId - Patron ID
 * @param euid - External unique identifier
 * @param preferences - Subscription preferences to update
 */
export const updateMerchantSubscription = async (
  transactionId?: string | undefined,
  euid?: string | undefined,
  preferences?: MerchantSubscriptionPreferences
): Promise<ApiResponse<void>> => {
  try {
    const response = await axiosInstance.put<ApiResponse<void>>(
      `emailmgmt/subscription/merchant`,
      {
        transactionId,
        preferences,
        ...(euid?.trim() ? { euid: euid.trim() } : {})
      }
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error,
      "Failed to update merchant subscription preferences"
    );
  }
};

/**
 * Fetch Rewardle subscription preferences
 * @param transactionId - Optional transaction ID from email log
 * @param euid - External unique identifier
 */
export const getRewardleSubscription = async (
  transactionId?: string,
  euid?: string
): Promise<RewardleSubscriptionData> => {
  try {
    const params: Record<string, string> = {};
    if (transactionId?.trim()) params.transactionId = transactionId.trim();
    if (euid?.trim()) params.euid = euid.trim();

    const response = await axiosInstance.get<RewardleSubscriptionData>(
      `emailmgmt/subscription/rewardle`,
      {
        params
      }
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error,
      "Failed to load Rewardle subscription preferences"
    );
  }
};

/**
 * Update Rewardle subscription preferences
 * @param email - Patron email address
 * @param euid - External unique identifier
 * @param preferences - Subscription preferences to update
 */
export const updateRewardleSubscription = async (
  email: string,
  euid: string | undefined,
  preferences: RewardleSubscriptionPreferences
): Promise<ApiResponse<void>> => {
  try {
    const response = await axiosInstance.put<ApiResponse<void>>(
      `emailmgmt/subscription/rewardle`,
      {
        email,
        preferences,
        ...(euid?.trim() ? { euid: euid.trim() } : {})
      }
    );
    return response.data;
  } catch (error) {
    return handleApiError(
      error,
      "Failed to update Rewardle subscription preferences"
    );
  }
};

/**
 * Fetch email transaction log by ID
 * @param transactionId - Transaction ID from email log
 */
export const getEmailLog = async (
  transactionId: string
): Promise<EmailLogData> => {
  try {
    const response = await axiosInstance.get<EmailLogData>(
      `emailmgmt/subscription/emaillog/${transactionId}`
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, "Failed to load email transaction log");
  }
};
